import { useEffect, useMemo } from 'react'
import { View, ActivityIndicator } from 'react-native'
import { useRouter, type Href } from 'expo-router'
import { useTheme } from '../../../theme/useTheme'
import { useImpersonation } from '../../hooks/useImpersonation'
import { setImpersonationSession } from '../../helpers/impersonationToken.helper'
import { UNIMPERSONATE_NAV_FAILSAFE_MS } from '../../constants/impersonationRoute.constants'
import { getImpersonationUrlHandlerStyles } from '../ImpersonationUrlHandler/ImpersonationUrlHandler.styles'

const APP_HOME_HREF = '/app' as Href

interface UnimpersonateUrlHandlerProps {
  /** Where to land after impersonation ends. Defaults to `/app`. */
  afterReplaceHref?: Href
}

/**
 * Headless route `/app/unimpersonate` — ends impersonation then replaces to `afterReplaceHref` (default `/app`).
 */
export function UnimpersonateUrlHandler({
  afterReplaceHref = APP_HOME_HREF,
}: UnimpersonateUrlHandlerProps = {}) {
  const theme = useTheme()
  const styles = useMemo(() => getImpersonationUrlHandlerStyles(theme), [theme])
  const router = useRouter()
  const { exitImpersonation } = useImpersonation()

  useEffect(() => {
    let cancelled = false
    let finished = false
    const goHome = () => {
      if (cancelled || finished) return
      finished = true
      router.replace(afterReplaceHref)
    }

    const failsafeId = setTimeout(() => {
      if (cancelled) return
      setImpersonationSession(null)
      goHome()
    }, UNIMPERSONATE_NAV_FAILSAFE_MS)

    void exitImpersonation().finally(() => {
      if (cancelled) return
      clearTimeout(failsafeId)
      goHome()
    })

    return () => {
      cancelled = true
      clearTimeout(failsafeId)
    }
  }, [afterReplaceHref, exitImpersonation, router])

  return (
    <View style={styles.busyWrap}>
      <ActivityIndicator size="large" color={theme.primary} />
    </View>
  )
}
